import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import StandardizedChip from '../../../common/StandardizedChip';
import {
  Feedback as FeedbackIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { formatPercentage } from './utils';

/**
 * FeedbackMetricsCard Component
 *
 * Displays feedback metrics for a version.
 *
 * @param {Object} props
 * @param {Object} props.selectedVersion - Selected version data
 */
function FeedbackMetricsCard({ selectedVersion }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <FeedbackIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Feedback Metrics</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box textAlign="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <StarIcon color="warning" />
                <Typography variant="h4" color="warning.main">
                  {selectedVersion.avg_rating
                    ? selectedVersion.avg_rating.toFixed(2)
                    : 'N/A'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Average Rating
                {selectedVersion.avg_rating &&
                  ` (${selectedVersion.avg_rating.toFixed(1)}/5.0)`}
              </Typography>
              {selectedVersion.min_rating !== undefined &&
                selectedVersion.max_rating !== undefined && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Range: {selectedVersion.min_rating} -{' '}
                    {selectedVersion.max_rating}
                  </Typography>
                )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary.main">
                {selectedVersion.total_feedback_count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Feedback Count
              </Typography>
              {selectedVersion.positive_feedback_count !== undefined &&
                selectedVersion.negative_feedback_count !== undefined && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Positive: {selectedVersion.positive_feedback_count || 0} /
                    Negative: {selectedVersion.negative_feedback_count || 0}
                  </Typography>
                )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box textAlign="center">
              <StandardizedChip
                label={formatPercentage(
                  selectedVersion.feedback_coverage_percentage
                )}
                color={
                  selectedVersion.feedback_coverage_percentage >= 50
                    ? 'success'
                    : selectedVersion.feedback_coverage_percentage >= 20
                      ? 'warning'
                      : 'default'
                }
                width={100}
                height={40}
                sx={{ fontSize: '1.25rem', py: 2, px: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Feedback Coverage
              </Typography>
            </Box>
          </Grid>
          {selectedVersion.weighted_rating_score !== undefined && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Weighted Rating Score
                </Typography>
                <Typography variant="h6">
                  {selectedVersion.weighted_rating_score.toFixed(4)}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default FeedbackMetricsCard;
